import { findRegimen, type Plan, type Regimen } from "@/data/meal-plan";
import { createEmptyWeek, type DayOfWeek, type DayPlan, type RegimenPresets, type WeekPlan } from "@/types/meal-plan-state";
import { migrateWeekPlan } from "./migrate-plan";
import { createEmptyDaySelections } from "./random-plan";
import { getWeekDates } from "./week-utils";

/**
 * Weekday presets as seen under `plan`: every preset that isn't one of the
 * plan's regimens, unassigned ones included, reads as the plan's
 * `defaultRegimenId`, so new weeks and adopted days land in the plan. Applied
 * when presets are read (the stored presets never change on a plan switch),
 * so switching back to a plan restores its custom weekdays.
 */
export function remapPresetsForPlan(presets: RegimenPresets, plan: Plan): RegimenPresets {
  const inPlan = new Set(plan.regimens.map((r) => r.id));
  const remapped = { ...presets };
  for (const day of Object.keys(remapped) as DayOfWeek[]) {
    const id = remapped[day];
    if (!id || !inPlan.has(id)) remapped[day] = plan.defaultRegimenId;
  }
  return remapped;
}

/** True when the user has put nothing into the day: no picks, skips, menus or notes. */
export function isDayUntouched(day: DayPlan): boolean {
  const hasPicksOrSkips = day.meals?.some((meal) =>
    meal.some((cg) => cg.skipped || cg.selections.some((s) => s.count > 0)),
  );
  const hasMenus = Object.keys(day.mealMenus ?? {}).length > 0;
  const hasNotes = Object.values(day.mealNotes ?? {}).some((note) => String(note).trim() !== "");
  return !hasPicksOrSkips && !hasMenus && !hasNotes;
}

/**
 * Move a week's untouched days from `today` (YYYY-MM-DD, local) on into the
 * user's current plan: a day whose regimen isn't one of `planRegimens` (or
 * has none) and that has nothing planned switches to its weekday preset with
 * fresh selections. Past days and days with anything planned keep their
 * regimen, as do days whose preset isn't in the plan (nothing to adopt).
 * Returns the same `weekPlan` object when nothing changed.
 */
export function adoptPlanForUnplannedDays(
  weekPlan: WeekPlan,
  weekId: string,
  today: string,
  planRegimens: Regimen[],
  presets: RegimenPresets,
): { plan: WeekPlan; adopted: boolean } {
  let plan = weekPlan;
  for (const { day, date } of getWeekDates(weekId)) {
    if (date < today) continue;
    const dayPlan = weekPlan[day];
    if (planRegimens.some((r) => r.id === dayPlan.regimenId)) continue;
    if (!isDayUntouched(dayPlan)) continue;
    const preset = planRegimens.find((r) => r.id === presets[day]);
    if (!preset) continue;
    plan = { ...plan, [day]: { regimenId: preset.id, meals: createEmptyDaySelections(preset) } };
  }
  return { plan, adopted: plan !== weekPlan };
}

/** Each weekday's preset regimen with fresh selections; days without a known preset stay empty. */
function fillDaysFromPresets(
  weekPlan: WeekPlan,
  presets: RegimenPresets,
  shouldFill: (day: DayPlan) => boolean,
): WeekPlan {
  let plan = weekPlan;
  for (const day of Object.keys(weekPlan) as DayOfWeek[]) {
    if (!shouldFill(weekPlan[day])) continue;
    const regimen = findRegimen(presets[day]);
    if (!regimen) continue;
    plan = { ...plan, [day]: { regimenId: regimen.id, meals: createEmptyDaySelections(regimen) } };
  }
  return plan;
}

/** `migrateWeekPlan` without mutating `stored`: the same object when nothing needed migrating. */
export function migrateStoredWeek(stored: WeekPlan): WeekPlan {
  const copy = structuredClone(stored);
  return migrateWeekPlan(copy).migrated ? copy : stored;
}

/**
 * Turn a week as stored on the server into the week to show, in order:
 * 1. a missing week is built from the weekday presets;
 * 2. legacy selections are migrated;
 * 3. untouched days from `today` on adopt the current plan;
 * 4. days still without a regimen (past ones included) get their preset.
 *
 * Pure: `stored` is never mutated, and when nothing changes the very same
 * object comes back, so callers can tell "nothing to save" by identity.
 */
export function prepareLoadedWeek(
  stored: WeekPlan | null,
  weekId: string,
  today: string,
  planRegimens: Regimen[],
  presets: RegimenPresets,
): WeekPlan {
  let plan = stored ? migrateStoredWeek(stored) : fillDaysFromPresets(createEmptyWeek(), presets, () => true);
  plan = adoptPlanForUnplannedDays(plan, weekId, today, planRegimens, presets).plan;
  return fillDaysFromPresets(plan, presets, (day) => !day.regimenId);
}
